const SEARCH_RESULT_SELECTOR = '.search-result'
const RETAINED_PAINT_STATE = 'retained'

function searchResults(container: HTMLElement): HTMLElement[] {
  return [...container.children]
    .map((child) => child.querySelector<HTMLElement>(SEARCH_RESULT_SELECTOR))
    .filter((result): result is HTMLElement => result !== null)
}

function retainPaint(result: HTMLElement): void {
  result.dataset.searchPaint = RETAINED_PAINT_STATE
}

function releasePaint(result: HTMLElement): void {
  delete result.dataset.searchPaint
}

/**
 * Keeps rich Search cards mounted while bounding the browser's active paint work.
 * Cards inside the observer margin are made fully paintable before they enter the
 * viewport; distant cards fall back to CSS content-visibility:auto.
 */
export function installSearchResultPaintWindow(
  container: HTMLElement,
  marginPercent: number,
): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    const results = searchResults(container)
    results.forEach(retainPaint)
    return () => results.forEach(releasePaint)
  }

  const observed = new Set<HTMLElement>()
  const intersectionObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!(entry.target instanceof HTMLElement)) continue
      if (entry.isIntersecting) retainPaint(entry.target)
      else releasePaint(entry.target)
    }
  }, {
    rootMargin: `${marginPercent}% 0px`,
    threshold: 0,
  })

  const synchronizeResults = () => {
    const current = new Set(searchResults(container))
    for (const result of observed) {
      if (current.has(result)) continue
      intersectionObserver.unobserve(result)
      releasePaint(result)
      observed.delete(result)
    }
    for (const result of current) {
      if (observed.has(result)) continue
      observed.add(result)
      intersectionObserver.observe(result)
    }
  }

  synchronizeResults()
  const mutationObserver = typeof MutationObserver === 'undefined'
    ? null
    : new MutationObserver(synchronizeResults)
  mutationObserver?.observe(container, { childList: true })

  return () => {
    mutationObserver?.disconnect()
    intersectionObserver.disconnect()
    for (const result of observed) releasePaint(result)
  }
}
